import Downshift, { StateChangeTypes } from 'downshift';
import React, { FC, KeyboardEvent } from 'react';
import { InputContainer, InputField, InputItem, InputMenu, Wrapper } from './inputWithAutocomplete.styles';

interface Props {
	value: string;
	placeholder: string;
	options: any;
	onChange: (val: string) => void;
	onFocusLose: () => void;
	onDropdownSelection?: (selectedItem: string) => void;
	autocompleteIsOpen: boolean;
}

const InputWithAutocomplete: FC<Props> = ({
	options,
	onDropdownSelection,
	onChange,
	onFocusLose,
	value,
	placeholder,
	autocompleteIsOpen,
}) => {
	const onInputChange = (inputValue: string, stateHelper: { type: string | number }) => {
		if (value !== inputValue && !['__autocomplete_mouseup__', '1'].includes(`${stateHelper.type}`)) {
			onChange(inputValue || '');
		}
	};

	const onInputKeyDown = (event: KeyboardEvent) => {
		if (event.keyCode === 9 && event.shiftKey) {
			onFocusLose();
		}
	};

	const onSelectionChange = selectValue => {
		if (selectValue) {
			onDropdownSelection(selectValue.name);
		}
	};

	const stateReducer = (state: { inputValue: string }, changes: { type: StateChangeTypes }) => {
		switch (changes.type) {
			case Downshift.stateChangeTypes.blurInput:
				return {
					...changes,
					isOpen: autocompleteIsOpen,
					inputValue: state.inputValue,
				};
			default:
				return changes;
		}
	};

	return (
		<Downshift
			inputValue={value}
			onChange={selectedItem => onSelectionChange(selectedItem)}
			onInputValueChange={(inputValue, stateHelper) =>
				onInputChange(inputValue, stateHelper as unknown as { type: string | number })
			}
			itemToString={item => (item ? item.name : '')}
			stateReducer={stateReducer}
			isOpen={autocompleteIsOpen}>
			{({ getInputProps, getMenuProps, getRootProps, getItemProps, isOpen, inputValue }) => (
				<Wrapper {...getRootProps()}>
					<InputContainer>
						<InputField
							placeholder={placeholder}
							value={value}
							{...getInputProps()}
							onKeyDown={onInputKeyDown}
							isActive={isOpen}
						/>
					</InputContainer>
					{isOpen && inputValue && options && (
						<InputMenu {...getMenuProps()} isActive={isOpen}>
							{options
								.filter(
									item =>
										!inputValue ||
										(item.name.toLowerCase().includes(inputValue.toLowerCase()) &&
											item.name.toLowerCase() !== inputValue.toLowerCase())
								)
								.map((item, index) => (
									<InputItem
										{...getItemProps({
											key: item.name,
											item,
											index,
										})}
										tabIndex={0}>
										{item.name}
									</InputItem>
								))}
						</InputMenu>
					)}
				</Wrapper>
			)}
		</Downshift>
	);
};

export default InputWithAutocomplete;
